/**
 * Palisade audio module — generative BGM + synthesized SE, Web Audio only.
 *
 * No audio assets, no dependencies. Everything is synthesized at runtime.
 * Safe to import in non-browser environments (tests / SSR): module top level
 * touches no browser globals, and createAudio() degrades to a no-op object
 * when Web Audio is unavailable.
 *
 * Public interface (see src/audio/INTEGRATION.md for wiring):
 *   const audio = createAudio();
 *   audio.unlock();        // call from a user-gesture handler (click)
 *   audio.startBgm();      // idempotent; ignored before unlock()
 *   audio.stopBgm();
 *   audio.playSe(name);    // one of SE_NAMES; ignored before unlock()
 *   audio.setMuted(bool);  // persisted to localStorage('palisade-muted')
 *   audio.muted            // current mute state (getter)
 *
 * Musical design (BGM):
 *   Hirajoshi pentatonic on A (A B C E F) — a traditional Japanese scale —
 *   played as a slow koto-like plucked arpeggio over a soft two-oscillator
 *   drone (root + fifth), with a gentle feedback delay for space.
 *   4 bars rotate (patterns + drone roots A / A / F / E), 8 eighth-note
 *   steps per bar at 42 BPM (~5.7 s per bar, ~23 s full cycle).
 *   A lookahead scheduler keeps reserving notes ahead of the clock, so the
 *   loop has no seam. Volume is deliberately low (BGM bus gain 0.16).
 *
 * Lifecycle:
 *   - Autoplay policy: the AudioContext is created/resumed in unlock();
 *     playSe/startBgm before unlock are silently ignored.
 *   - visibilitychange is handled internally: hidden -> ctx.suspend(),
 *     visible -> ctx.resume() (only after unlock; BGM continues only if
 *     startBgm() had been called — the scheduler simply picks up again).
 *   - SE are one-shot node graphs (create -> start -> auto-GC); everything
 *     routes through a master gain (mute) via per-bus gains (bgm / se).
 */

// ---------------------------------------------------------------------------
// Pure, Web-Audio-independent data & helpers (unit-tested in tests/audio.test.js)
// ---------------------------------------------------------------------------

export const STORAGE_KEY = 'palisade-muted';

export const SE_NAMES = Object.freeze([
  'select', // UI click / card selection
  'move', // pawn move (wooden tap)
  'wall', // wall placement (heavy thud)
  'jump', // face-to-face jump / ninja wallJump (whoosh + landing)
  'slide', // ice slide (descending sweep)
  'swamp', // stuck in swamp (low squelchy wobble)
  'ability', // generic ability (sparkle)
  'win', // victory jingle
  'illegal', // rejected input (quiet double buzz)
]);

export function isSeName(name) {
  return SE_NAMES.includes(name);
}

/** MIDI note number -> frequency in Hz (A4 = 69 = 440 Hz). */
export function midiToFreq(midi) {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

/** Hirajoshi scale (Japanese pentatonic): semitone offsets from the root. */
export const SCALE = Object.freeze([0, 2, 3, 7, 8]); // A B C E F on an A root

const BPM = 42;
const STEP_DUR = 60 / BPM / 2; // eighth notes: ~0.714 s
const STEPS_PER_BAR = 8;

/**
 * BGM sequence data. Each pattern cell is null (rest) or { d, o }:
 * d = scale degree index into SCALE, o = octave above rootMidi (A3 = 57).
 * barRoots are the drone MIDI notes per bar: A2, A2, F2, E2 — a slow
 * i - i - VI - v loop that resolves back to A each cycle.
 */
export const BGM = Object.freeze({
  bpm: BPM,
  stepDur: STEP_DUR,
  stepsPerBar: STEPS_PER_BAR,
  barDur: STEP_DUR * STEPS_PER_BAR,
  rootMidi: 57, // A3
  barRoots: Object.freeze([45, 45, 41, 40]), // A2, A2, F2, E2
  droneOverlap: 1.6, // seconds a drone rings past its bar (seamless crossfade)
  pluckDur: 1.2, // decay time of one plucked note
  patterns: Object.freeze([
    // bar 0 — calm ascent over the A drone
    Object.freeze([
      { d: 0, o: 1 }, null, { d: 1, o: 1 }, null,
      { d: 3, o: 1 }, null, null, { d: 2, o: 1 },
    ]),
    // bar 1 — answer, one register up
    Object.freeze([
      { d: 3, o: 1 }, null, { d: 4, o: 1 }, null,
      { d: 0, o: 2 }, null, { d: 3, o: 1 }, null,
    ]),
    // bar 2 — F-major colors (C F C A) over the F drone
    Object.freeze([
      { d: 2, o: 1 }, null, null, { d: 4, o: 1 },
      null, { d: 2, o: 1 }, { d: 0, o: 1 }, null,
    ]),
    // bar 3 — sparse suspended cadence (B A B) over the E drone
    Object.freeze([
      { d: 1, o: 1 }, null, { d: 0, o: 1 }, null,
      null, { d: 1, o: 0 }, null, null,
    ]),
  ]),
});

/**
 * MIDI note played at (bar, step), or null for a rest.
 * bar rotates over BGM.patterns; step indexes within a pattern.
 */
export function noteAt(bar, step) {
  const pattern = BGM.patterns[bar % BGM.patterns.length];
  const cell = pattern[step % BGM.stepsPerBar];
  if (!cell) return null;
  return BGM.rootMidi + SCALE[cell.d] + 12 * cell.o;
}

/** Sequencer cursor for the lookahead scheduler. */
export function createSequencer(startTime) {
  return { nextTime: startTime, bar: 0, step: 0 };
}

/**
 * If the clock ran far past the cursor (tab was suspended, long GC pause),
 * jump the cursor forward instead of "catching up" with a burst of notes.
 * Returns true if a resync happened. Mutates seq.
 */
export function resyncIfLagging(seq, now, maxLag = 0.5) {
  if (now - seq.nextTime > maxLag) {
    seq.nextTime = now + 0.05;
    return true;
  }
  return false;
}

/**
 * Advance the sequencer, returning every event whose time falls inside
 * [now, now + lookahead). Mutates seq. Events:
 *   { kind: 'drone', time, midi, dur }  — one per bar, at step 0
 *   { kind: 'pluck', time, midi, dur }  — melody notes (rests skipped)
 */
export function collectDueEvents(seq, now, lookahead) {
  const events = [];
  while (seq.nextTime < now + lookahead) {
    if (seq.step === 0) {
      events.push({
        kind: 'drone',
        time: seq.nextTime,
        midi: BGM.barRoots[seq.bar % BGM.barRoots.length],
        dur: BGM.barDur + BGM.droneOverlap,
      });
    }
    const midi = noteAt(seq.bar, seq.step);
    if (midi !== null) {
      events.push({ kind: 'pluck', time: seq.nextTime, midi, dur: BGM.pluckDur });
    }
    seq.step += 1;
    if (seq.step >= BGM.stepsPerBar) {
      seq.step = 0;
      seq.bar = (seq.bar + 1) % BGM.patterns.length;
    }
    seq.nextTime += BGM.stepDur;
  }
  return events;
}

/** Read persisted mute flag ('1' = muted). Never throws. */
export function readMuted(storage) {
  try {
    return !!storage && storage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/** Persist mute flag. Never throws (private mode / quota errors ignored). */
export function writeMuted(storage, muted) {
  try {
    if (storage) storage.setItem(STORAGE_KEY, muted ? '1' : '0');
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// Environment defaults (guarded — safe under Node / SSR)
// ---------------------------------------------------------------------------

function defaultAudioContextClass() {
  if (typeof window === 'undefined') return null;
  return window.AudioContext || window.webkitAudioContext || null;
}

function defaultStorage() {
  try {
    if (typeof localStorage !== 'undefined') return localStorage;
  } catch {
    /* ignore */
  }
  return null;
}

function defaultDocument() {
  return typeof document !== 'undefined' ? document : null;
}

// ---------------------------------------------------------------------------
// Gain staging
// ---------------------------------------------------------------------------

const MASTER_LEVEL = 1.0; // mute toggles this bus to 0
const BGM_LEVEL = 0.16; // quiet, non-intrusive background
const SE_LEVEL = 0.5;

const SCHEDULER_INTERVAL_MS = 50;
const SCHEDULER_LOOKAHEAD_S = 0.18;

// ---------------------------------------------------------------------------
// createAudio
// ---------------------------------------------------------------------------

/**
 * Create the audio singleton. Options (all optional, mainly for tests):
 *   storage            — localStorage-like object (getItem/setItem)
 *   AudioContextClass  — constructor to use instead of window.AudioContext
 *   document           — document-like target for visibilitychange
 */
export function createAudio(options = {}) {
  const storage = options.storage !== undefined ? options.storage : defaultStorage();
  const AudioContextClass =
    options.AudioContextClass !== undefined
      ? options.AudioContextClass
      : defaultAudioContextClass();
  const doc = options.document !== undefined ? options.document : defaultDocument();

  let ctx = null;
  let masterGain = null;
  let bgmBus = null;
  let seBus = null;
  let noiseBuffer = null;

  let unlocked = false;
  let muted = readMuted(storage);

  let bgmPlaying = false;
  let bgmTimer = null;
  let seq = null;
  const bgmSources = new Set(); // live BGM source nodes, for stopBgm()

  // ---- graph -------------------------------------------------------------

  function ensureContext() {
    if (ctx || !AudioContextClass) return ctx;
    try {
      ctx = new AudioContextClass();
    } catch {
      return null;
    }

    masterGain = ctx.createGain();
    masterGain.gain.value = muted ? 0 : MASTER_LEVEL;
    masterGain.connect(ctx.destination);

    seBus = ctx.createGain();
    seBus.gain.value = SE_LEVEL;
    seBus.connect(masterGain);

    bgmBus = ctx.createGain();
    bgmBus.gain.value = BGM_LEVEL;
    bgmBus.connect(masterGain);

    // Gentle feedback delay on the BGM bus — adds "ma" (space) to the plucks.
    try {
      const delay = ctx.createDelay(1.0);
      delay.delayTime.value = 0.45;
      const feedback = ctx.createGain();
      feedback.gain.value = 0.3;
      const wet = ctx.createGain();
      wet.gain.value = 0.35;
      const damp = ctx.createBiquadFilter();
      damp.type = 'lowpass';
      damp.frequency.value = 2200;
      bgmBus.connect(delay);
      delay.connect(damp);
      damp.connect(feedback);
      feedback.connect(delay);
      damp.connect(wet);
      wet.connect(masterGain);
    } catch {
      /* delay is decorative; keep going without it */
    }

    return ctx;
  }

  function getNoiseBuffer() {
    if (noiseBuffer) return noiseBuffer;
    const length = Math.max(1, Math.floor(ctx.sampleRate * 0.5));
    noiseBuffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = noiseBuffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    return noiseBuffer;
  }

  // ---- one-shot building blocks -------------------------------------------
  // All envelopes start/end at 0.0001 (exponential ramps cannot touch 0).

  function envelope(dest, t, peak, attack, decay) {
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(peak, t + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
    g.connect(dest);
    return g;
  }

  function tone(dest, t, { type = 'sine', freq, freqEnd, peak, attack = 0.004, decay, track = false }) {
    const o = ctx.createOscillator();
    o.type = type;
    o.frequency.setValueAtTime(freq, t);
    if (freqEnd !== undefined) {
      o.frequency.exponentialRampToValueAtTime(Math.max(1, freqEnd), t + attack + decay);
    }
    o.connect(envelope(dest, t, peak, attack, decay));
    o.start(t);
    o.stop(t + attack + decay + 0.05);
    if (track) trackBgmSource(o);
    return o;
  }

  function noise(dest, t, { peak, attack = 0.002, decay, filterType, filterFreq, filterFreqEnd, q = 1 }) {
    const src = ctx.createBufferSource();
    src.buffer = getNoiseBuffer();
    src.loop = true;
    let head = src;
    if (filterType) {
      const f = ctx.createBiquadFilter();
      f.type = filterType;
      f.frequency.setValueAtTime(filterFreq, t);
      if (filterFreqEnd !== undefined) {
        f.frequency.exponentialRampToValueAtTime(Math.max(1, filterFreqEnd), t + attack + decay);
      }
      f.Q.value = q;
      src.connect(f);
      head = f;
    }
    head.connect(envelope(dest, t, peak, attack, decay));
    src.start(t);
    src.stop(t + attack + decay + 0.05);
    return src;
  }

  function trackBgmSource(node) {
    bgmSources.add(node);
    node.onended = () => bgmSources.delete(node);
  }

  // ---- BGM voices ----------------------------------------------------------

  function renderPluck(ev) {
    const freq = midiToFreq(ev.midi);
    // Koto-ish pluck: triangle body + faint sine an octave up for attack shine.
    tone(bgmBus, ev.time, { type: 'triangle', freq, peak: 0.5, attack: 0.005, decay: ev.dur, track: true });
    tone(bgmBus, ev.time, { type: 'sine', freq: freq * 2, peak: 0.12, attack: 0.003, decay: ev.dur * 0.4, track: true });
  }

  function renderDrone(ev) {
    const freq = midiToFreq(ev.midi);
    const t = ev.time;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(0.35, t + 1.4);
    g.gain.setValueAtTime(0.35, t + ev.dur - 1.4);
    g.gain.linearRampToValueAtTime(0.0001, t + ev.dur);
    g.connect(bgmBus);

    const root = ctx.createOscillator();
    root.type = 'sine';
    root.frequency.setValueAtTime(freq, t);
    root.connect(g);

    const fifthGain = ctx.createGain();
    fifthGain.gain.value = 0.3;
    fifthGain.connect(g);
    const fifth = ctx.createOscillator();
    fifth.type = 'sine';
    fifth.frequency.setValueAtTime(freq * 1.5, t);
    fifth.connect(fifthGain);

    root.start(t);
    root.stop(t + ev.dur + 0.05);
    fifth.start(t);
    fifth.stop(t + ev.dur + 0.05);
    trackBgmSource(root);
    trackBgmSource(fifth);
  }

  function schedulerTick() {
    if (!ctx || !bgmPlaying) return;
    resyncIfLagging(seq, ctx.currentTime);
    const events = collectDueEvents(seq, ctx.currentTime, SCHEDULER_LOOKAHEAD_S);
    for (const ev of events) {
      if (ev.kind === 'drone') renderDrone(ev);
      else renderPluck(ev);
    }
  }

  // ---- SE renderers ---------------------------------------------------------

  const SE_RENDERERS = {
    // Short soft blip: sine 880 -> 660.
    select(t) {
      tone(seBus, t, { type: 'sine', freq: 880, freqEnd: 660, peak: 0.4, attack: 0.003, decay: 0.07 });
    },

    // Wooden tap: tiny high noise click + low triangle knock.
    move(t) {
      noise(seBus, t, { peak: 0.3, decay: 0.03, filterType: 'bandpass', filterFreq: 2000, q: 1.5 });
      tone(seBus, t, { type: 'triangle', freq: 170, freqEnd: 120, peak: 0.5, attack: 0.004, decay: 0.09 });
    },

    // Heavy thud: pitched-down sine + low noise body.
    wall(t) {
      tone(seBus, t, { type: 'sine', freq: 130, freqEnd: 50, peak: 0.9, attack: 0.005, decay: 0.22 });
      noise(seBus, t, { peak: 0.45, decay: 0.09, filterType: 'lowpass', filterFreq: 500 });
    },

    // Whoosh (rising band-passed noise) then a landing knock.
    jump(t) {
      noise(seBus, t, { peak: 0.3, attack: 0.02, decay: 0.13, filterType: 'bandpass', filterFreq: 500, filterFreqEnd: 2600, q: 1.2 });
      tone(seBus, t + 0.16, { type: 'triangle', freq: 160, freqEnd: 110, peak: 0.45, attack: 0.004, decay: 0.08 });
    },

    // Icy slide: long descending noise sweep.
    slide(t) {
      noise(seBus, t, { peak: 0.32, attack: 0.02, decay: 0.38, filterType: 'bandpass', filterFreq: 3200, filterFreqEnd: 550, q: 1 });
    },

    // Squelchy wobble: LFO-modulated low sine dropping in pitch + muddy noise.
    swamp(t) {
      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.setValueAtTime(150, t);
      o.frequency.exponentialRampToValueAtTime(65, t + 0.32);
      const lfo = ctx.createOscillator();
      lfo.frequency.setValueAtTime(9, t);
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = 28;
      lfo.connect(lfoGain);
      lfoGain.connect(o.frequency);
      o.connect(envelope(seBus, t, 0.55, 0.01, 0.32));
      o.start(t);
      o.stop(t + 0.4);
      lfo.start(t);
      lfo.stop(t + 0.4);
      noise(seBus, t, { peak: 0.25, decay: 0.1, filterType: 'lowpass', filterFreq: 320 });
    },

    // Magic sparkle: staggered high pentatonic chimes + shimmer.
    ability(t) {
      const chimes = [1318.5, 1760, 2637]; // E6, A6, E7
      chimes.forEach((freq, i) => {
        tone(seBus, t + i * 0.06, { type: 'sine', freq, peak: 0.25, attack: 0.004, decay: 0.35 });
      });
      tone(seBus, t, { type: 'triangle', freq: 880, peak: 0.15, attack: 0.01, decay: 0.4 });
    },

    // Bright short fanfare: A4 D5 E5 A5 with octave doubling on the last.
    win(t) {
      const notes = [440, 587.33, 659.25, 880];
      notes.forEach((freq, i) => {
        const at = t + i * 0.13;
        const last = i === notes.length - 1;
        tone(seBus, at, { type: 'triangle', freq, peak: 0.35, attack: 0.006, decay: last ? 0.7 : 0.22 });
        if (last) {
          tone(seBus, at, { type: 'sine', freq: freq * 2, peak: 0.15, attack: 0.006, decay: 0.7 });
        }
      });
    },

    // Restrained double buzz — audible but not annoying.
    illegal(t) {
      tone(seBus, t, { type: 'square', freq: 110, peak: 0.14, attack: 0.004, decay: 0.05 });
      tone(seBus, t + 0.09, { type: 'square', freq: 98, peak: 0.14, attack: 0.004, decay: 0.06 });
    },
  };

  // ---- visibility handling (automatic, module-internal) ----------------------

  if (doc && typeof doc.addEventListener === 'function') {
    doc.addEventListener('visibilitychange', () => {
      if (!ctx) return;
      try {
        if (doc.visibilityState === 'hidden') {
          if (ctx.state === 'running') ctx.suspend();
        } else if (unlocked) {
          // ctx.currentTime was frozen while suspended, so the scheduler simply
          // resumes reserving notes; BGM continues only if startBgm() had run.
          ctx.resume();
        }
      } catch {
        /* ignore */
      }
    });
  }

  // ---- public interface -------------------------------------------------------

  const audio = {
    get muted() {
      return muted;
    },

    /** Call from a user-gesture handler (click). Enables all playback. */
    unlock() {
      const c = ensureContext();
      if (!c) return;
      unlocked = true;
      try {
        if (c.state !== 'running' && typeof c.resume === 'function') c.resume();
      } catch {
        /* ignore */
      }
    },

    /** Start the generative BGM loop. Safe to call repeatedly. */
    startBgm() {
      if (!unlocked || !ctx || bgmPlaying) return;
      bgmPlaying = true;
      bgmBus.gain.setTargetAtTime(BGM_LEVEL, ctx.currentTime, 0.05);
      seq = createSequencer(ctx.currentTime + 0.08);
      schedulerTick(); // immediate first fill, then keep the lookahead topped up
      bgmTimer = setInterval(schedulerTick, SCHEDULER_INTERVAL_MS);
    },

    stopBgm() {
      if (!bgmPlaying) return;
      bgmPlaying = false;
      if (bgmTimer !== null) {
        clearInterval(bgmTimer);
        bgmTimer = null;
      }
      if (!ctx) return;
      const t = ctx.currentTime;
      bgmBus.gain.setTargetAtTime(0.0001, t, 0.08);
      for (const node of bgmSources) {
        try {
          node.stop(t + 0.35);
        } catch {
          /* already stopped */
        }
      }
      bgmSources.clear();
    },

    /** Play a named sound effect. Unknown names / pre-unlock calls are ignored. */
    playSe(name) {
      if (!unlocked || !ctx || muted || !isSeName(name)) return;
      try {
        SE_RENDERERS[name](ctx.currentTime);
      } catch {
        /* a failed one-shot must never break the game */
      }
    },

    /** Mute/unmute everything; persisted across sessions. */
    setMuted(value) {
      muted = !!value;
      writeMuted(storage, muted);
      if (ctx && masterGain) {
        masterGain.gain.setTargetAtTime(muted ? 0 : MASTER_LEVEL, ctx.currentTime, 0.02);
      }
    },
  };

  return audio;
}
